import React, { useState, useEffect, useRef } from 'react';
import {
  MdPlayArrow,
  MdPause,
  MdClose,
  MdAdd,
  MdSkipNext,
  MdSkipPrevious,
  MdVolumeUp,
  MdOutlineAlbum,
} from 'react-icons/md';
import { fetchPublicAlbums } from './api/studioApi';
import { normalizeMediaUrl } from './config';
import './AlbumsPage.css';

const GENRE_FILTERS = ['All', 'Pop', 'Dance', 'Rock', 'Hip Hop', 'Electronic', 'R&B', 'Acoustic', 'Lo-Fi'];

export default function AlbumsPage({ userProfile, isLoggedIn, onLogin, onNavigate }) {
  const [albums, setAlbums] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [activeAlbumModal, setActiveAlbumModal] = useState(null);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioPlayerRef = useRef(null);

  const isUserAuthenticated = () => {
    if (typeof isLoggedIn === 'boolean') return isLoggedIn;
    if (typeof window !== 'undefined' && localStorage.getItem('speedotube_logged_out') === 'true') {
      return false;
    }
    if (userProfile && (userProfile.id || userProfile.token || userProfile.userId)) {
      return true;
    }
    const token = typeof window !== 'undefined' ? localStorage.getItem('speedotube_user_token') : null;
    const account = typeof window !== 'undefined' ? localStorage.getItem('speedotube_user_account') : null;
    return Boolean(token && account && localStorage.getItem('speedotube_logged_out') !== 'true');
  };

  const loadAlbums = async (genre = selectedGenre) => {
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('speedotube_channel_albums')) {
          try {
            const raw = localStorage.getItem(k);
            if (raw && raw.toLowerCase().includes('apt')) {
              const cleaned = JSON.parse(raw).filter((a) => !a.id?.toString().toLowerCase().includes('apt') && !a.title?.toLowerCase().includes('apt'));
              localStorage.setItem(k, JSON.stringify(cleaned));
            }
          } catch (e) {}
        }
      });
    } catch (e) {}

    const list = await fetchPublicAlbums(genre);
    setAlbums(list.filter((a) => !a.id?.toString().toLowerCase().includes('apt') && !a.title?.toLowerCase().includes('apt')));
  };

  useEffect(() => {
    loadAlbums(selectedGenre);
  }, [selectedGenre]);

  // Synchronize browser tab title with current playing track or albums page
  useEffect(() => {
    if (isPlaying && currentTrack?.title) {
      document.title = `${currentTrack.title}${currentTrack.artist ? ` - ${currentTrack.artist}` : ''} - SpeedoTube`;
    } else {
      document.title = 'Speedo Music & Albums - SpeedoTube';
    }
  }, [isPlaying, currentTrack?.title, currentTrack?.artist]);

  useEffect(() => {
    const handleRefresh = () => loadAlbums(selectedGenre);
    window.addEventListener('speedotube_album_published', handleRefresh);
    return () => window.removeEventListener('speedotube_album_published', handleRefresh);
  }, [selectedGenre]);

  const filteredAlbums = albums.filter((alb) => {
    if (selectedGenre === 'All') return true;
    return (alb.genre || '').toLowerCase().includes(selectedGenre.toLowerCase());
  });

  const handlePlayTrack = (track, album) => {
    if (currentTrack?.id === track.id) {
      if (isPlaying) {
        audioPlayerRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioPlayerRef.current?.play().catch(() => {});
        setIsPlaying(true);
      }
    } else {
      setCurrentTrack({ ...track, albumTitle: album.title, albumCover: album.coverUrl || album.playboxBannerUrl, artist: album.artist });
      if (audioPlayerRef.current) {
        const trackSrc = normalizeMediaUrl(track.audioUrl || track.audio_url || track.url);
        if (trackSrc) {
          audioPlayerRef.current.src = trackSrc;
          audioPlayerRef.current.play().catch(() => {});
        }
      }
      setIsPlaying(true);
    }
  };

  const handleNextTrack = () => {
    if (!activeAlbumModal || !currentTrack) return;
    const tracks = activeAlbumModal.tracks || [];
    const idx = tracks.findIndex((t) => t.id === currentTrack.id);
    if (idx !== -1 && idx < tracks.length - 1) {
      handlePlayTrack(tracks[idx + 1], activeAlbumModal);
    }
  };

  const handlePrevTrack = () => {
    if (!activeAlbumModal || !currentTrack) return;
    const tracks = activeAlbumModal.tracks || [];
    const idx = tracks.findIndex((t) => t.id === currentTrack.id);
    if (idx > 0) {
      handlePlayTrack(tracks[idx - 1], activeAlbumModal);
    }
  };

  return (
    <div className="yt-albums-page">
      <audio
        ref={audioPlayerRef}
        onEnded={handleNextTrack}
        onPause={() => setIsPlaying(false)}
        onPlay={() => setIsPlaying(true)}
      />

      {/* Page Header */}
      <div className="yt-albums-header">
        <div className="yt-albums-title-group">
          <h1 className="yt-albums-main-title">Speedo Music & Albums</h1>
          <p className="yt-albums-subtitle">
            Explore authentic audio releases, official studio EPs, and creator music collections
          </p>
        </div>

        <div className="yt-albums-header-actions">
          <button
            className="yt-albums-upload-btn"
            onClick={() => {
              if (!isUserAuthenticated()) {
                if (onLogin) onLogin();
                else if (onNavigate) onNavigate('/auth');
                return;
              }
              if (onNavigate) onNavigate('/studio/upload-album');
            }}
          >
            <MdAdd size={20} />
            <span>Upload Music Album</span>
          </button>
        </div>
      </div>

      {/* Genre Filter Bar */}
      <div className="yt-albums-genre-bar">
        {GENRE_FILTERS.map((genre) => (
          <button
            key={genre}
            className={`yt-albums-genre-chip ${selectedGenre === genre ? 'active' : ''}`}
            onClick={() => setSelectedGenre(genre)}
          >
            {genre}
          </button>
        ))}
      </div>

      {/* Album Cards Grid */}
      <div className="yt-albums-grid">
        {filteredAlbums.length > 0 ? (
          filteredAlbums.map((album) => (
            <div
              key={album.id}
              className="yt-album-card"
              onClick={() => {
                if (onNavigate) {
                  onNavigate(`/watch?album=${album.albumUrlId || album.id}`);
                } else {
                  setActiveAlbumModal(album);
                }
              }}
            >
              <div className="yt-album-cover-box">
                <img
                  src={normalizeMediaUrl(album.coverUrl || album.playboxBannerUrl) || (process.env.PUBLIC_URL || '') + '/img/banner.png'}
                  alt={album.title}
                  className="yt-album-cover-img"
                  onError={(e) => {
                    e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
                  }}
                />
                <div
                  className="yt-album-play-overlay"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onNavigate) {
                      onNavigate(`/watch?album=${album.albumUrlId || album.id}`);
                    } else {
                      if (album.tracks && album.tracks.length > 0) {
                        handlePlayTrack(album.tracks[0], album);
                      }
                      setActiveAlbumModal(album);
                    }
                  }}
                  title="Play Album"
                >
                  <MdPlayArrow size={26} />
                </div>
              </div>

              <div className="yt-album-card-info">
                <div className="yt-album-card-title">{album.title}</div>
                <div className="yt-album-card-artist">{album.artist || 'Speedo Artist'}</div>
                <div className="yt-album-card-meta">
                  <span className="yt-album-genre-tag">{album.genre || 'Music'}</span>
                  <span>•</span>
                  <span>{album.releaseYear || '2024'}</span>
                  <span>•</span>
                  <span>{(album.tracks || []).length} songs</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="yt-albums-empty-box">
            <MdOutlineAlbum size={56} style={{ color: '#64748b' }} />
            <div className="yt-albums-empty-title">No Music Albums Found</div>
            <p className="yt-albums-empty-desc">
              No albums match this genre filter. Be the first creator to upload a music album to SpeedoTube!
            </p>
            <button
              className="yt-albums-upload-btn"
              onClick={() => {
                if (!isUserAuthenticated()) {
                  if (onLogin) onLogin();
                  else if (onNavigate) onNavigate('/auth');
                  return;
                }
                if (onNavigate) onNavigate('/studio/upload-album');
              }}
            >
              <MdAdd size={20} />
              <span>Upload Album</span>
            </button>
          </div>
        )}
      </div>

      {/* Album Detail / Interactive Tracklist Modal */}
      {activeAlbumModal && (
        <div className="yt-album-modal-backdrop" onClick={() => setActiveAlbumModal(null)}>
          <div className="yt-album-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="yt-album-modal-header">
              <div className="yt-album-modal-cover">
                <img
                  src={normalizeMediaUrl(activeAlbumModal.coverUrl || activeAlbumModal.playboxBannerUrl) || (process.env.PUBLIC_URL || '') + '/img/banner.png'}
                  alt={activeAlbumModal.title}
                  onError={(e) => {
                    e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
                  }}
                />
              </div>

              <div className="yt-album-modal-details">
                <span className="yt-album-modal-tag">Official Album &bull; Square Banner (1:1)</span>
                <h2 className="yt-album-modal-title">{activeAlbumModal.title}</h2>
                <div className="yt-album-modal-artist">{activeAlbumModal.artist}</div>
                <div className="yt-album-modal-sub">
                  <span>{activeAlbumModal.genre}</span>
                  <span> • </span>
                  <span>{activeAlbumModal.releaseYear || '2024'}</span>
                  <span> • </span>
                  <span>{(activeAlbumModal.tracks || []).length} Tracks</span>
                </div>
                {activeAlbumModal.description && (
                  <p className="yt-album-modal-desc">{activeAlbumModal.description}</p>
                )}
              </div>

              <button
                className="yt-album-modal-close"
                onClick={() => setActiveAlbumModal(null)}
                title="Close"
              >
                <MdClose size={20} />
              </button>
            </div>

            {/* Tracklist List */}
            <div className="yt-album-modal-tracks-scroll">
              {(activeAlbumModal.tracks || []).map((track, idx) => {
                const isThisTrackPlaying = currentTrack?.id === track.id && isPlaying;
                return (
                  <div
                    key={track.id || idx}
                    className={`yt-album-track-row ${isThisTrackPlaying ? 'playing' : ''}`}
                  >
                    <div className="yt-album-track-left">
                      <span className="yt-album-track-num">{track.trackNumber || idx + 1}</span>
                      <button
                        className="yt-album-track-play-btn"
                        onClick={() => handlePlayTrack(track, activeAlbumModal)}
                        title={isThisTrackPlaying ? 'Pause' : 'Play Song'}
                      >
                        {isThisTrackPlaying ? <MdPause size={18} /> : <MdPlayArrow size={18} />}
                      </button>
                      <span className="yt-album-track-name">{track.title}</span>
                    </div>
                    <span className="yt-album-track-dur">{track.duration || '03:15'}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Persistent Mini Player Bar at Bottom when a track is active */}
      {currentTrack && (
        <div className="yt-album-mini-player">
          <div className="yt-player-track-info">
            <img
              src={normalizeMediaUrl(currentTrack.albumCover) || (process.env.PUBLIC_URL || '') + '/img/banner.png'}
              alt={currentTrack.title}
              className="yt-player-thumb"
              onError={(e) => {
                e.currentTarget.src = (process.env.PUBLIC_URL || '') + '/img/banner.png';
              }}
            />
            <div className="yt-player-meta">
              <span className="yt-player-title">{currentTrack.title}</span>
              <span className="yt-player-sub">{currentTrack.artist || currentTrack.albumTitle}</span>
            </div>
          </div>

          <div className="yt-player-controls">
            <button className="yt-player-ctrl-btn" onClick={handlePrevTrack} title="Previous Track">
              <MdSkipPrevious size={22} />
            </button>
            <button
              className="yt-player-ctrl-btn play-circle"
              onClick={() => {
                if (isPlaying) {
                  audioPlayerRef.current?.pause();
                  setIsPlaying(false);
                } else {
                  audioPlayerRef.current?.play().catch(() => {});
                  setIsPlaying(true);
                }
              }}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <MdPause size={24} /> : <MdPlayArrow size={24} />}
            </button>
            <button className="yt-player-ctrl-btn" onClick={handleNextTrack} title="Next Track">
              <MdSkipNext size={22} />
            </button>
          </div>

          <div className="yt-player-right">
            <MdVolumeUp size={20} style={{ color: '#94a3b8' }} />
            <button
              className="yt-player-close-btn"
              onClick={() => {
                audioPlayerRef.current?.pause();
                setCurrentTrack(null);
                setIsPlaying(false);
              }}
              title="Close Player"
            >
              <MdClose size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
